import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import Modal from '../components/ui/Modal';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { getApiBaseUrl } from '../apiBase';
import DemoViewBanner from '../demo/DemoViewBanner';

const API = getApiBaseUrl();

const RATE_TYPES = ['hourly', 'per_lesson', 'monthly', 'percentage'];
const RATE_UNIT_KEY = {
    hourly: 'rateUnitHourly', per_lesson: 'rateUnitPerLesson',
    monthly: 'rateUnitMonthly', percentage: 'rateUnitPercentage'
};
const RATE_LABEL_KEY = {
    hourly: 'rateHourly', per_lesson: 'ratePerLesson',
    monthly: 'rateMonthly', percentage: 'ratePercentage'
};
const TX_TYPE_KEY = {
    lesson_earned: 'typeLessonEarned', monthly_earned: 'typeMonthlyEarned',
    manual_earned: 'typeManualEarned', settled: 'typeSettled'
};

function firstDayOfMonth() {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function todayStr() {
    return new Date().toISOString().slice(0, 10);
}
function money(v) {
    return (Number(v) || 0).toLocaleString('ru-RU');
}

export default function PayrollPage() {
    const { t } = useTranslation(['payroll', 'common', 'staffManager', 'errors']);
    const roleLabel = (role) => {
        if (role === 'assistant') return t('staffManager:roleAssistantShort');
        if (role === 'tech') return t('staffManager:roleTech');
        if (role === 'admin') return t('staffManager:roleAdmin');
        if (role === 'other') return t('staffManager:roleOther');
        return t('staffManager:roleCoach');
    };

    const [school, setSchool] = useState(null);
    const [payrollEnabled, setPayrollEnabled] = useState(false);
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [activeTab, setActiveTab] = useState('balances');
    const [savingSettings, setSavingSettings] = useState(false);

    // action modal (earn / settle / rate / history)
    const [modal, setModal] = useState(null); // { type, staff }
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [rateType, setRateType] = useState('per_lesson');
    const [rateAmount, setRateAmount] = useState('');
    const [rateFrom, setRateFrom] = useState(todayStr());
    const [rateHistory, setRateHistory] = useState([]);
    const [balanceHistory, setBalanceHistory] = useState([]);
    const [busy, setBusy] = useState(false);

    // reports
    const [reportTab, setReportTab] = useState('lessons');
    const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
    const [dateTo, setDateTo] = useState(todayStr);
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    const loadAll = useCallback(async () => {
        setLoading(true);
        setErr('');
        try {
            const schoolRes = await getSchoolForSession(api);
            const schoolId = getSchoolIdFromResponse(schoolRes);
            setSchool(schoolRes.data);

            const [settingsRes, balancesRes] = await Promise.all([
                api.get('/staff-payroll/settings', { params: { schoolId } }),
                api.get('/staff-payroll/balances', { params: { schoolId } })
            ]);
            setPayrollEnabled(!!settingsRes.data?.payrollEnabled);
            setBalances(balancesRes.data || []);
        } catch (e) {
            setErr(e.response?.status === 403 ? t('payroll:errNoAccess') : (e.response?.data?.message || t('payroll:errLoad')));
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    useEffect(() => {
        if (activeTab !== 'reports' || !school?.id) return;
        loadReport(reportTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, reportTab, school?.id]);

    async function loadReport(kind) {
        if (!school?.id) return;
        setReportLoading(true);
        try {
            const res = await api.get(`/staff-payroll/reports/${kind}`, {
                params: { schoolId: school.id, dateFrom, dateTo }
            });
            setReportData(res.data);
        } catch (e) {
            setReportData(null);
        }
        setReportLoading(false);
    }

    async function toggleEnabled() {
        if (!school?.id) return;
        setSavingSettings(true);
        try {
            const res = await api.put('/staff-payroll/settings', {
                schoolId: school.id, payrollEnabled: !payrollEnabled
            });
            setPayrollEnabled(!!res.data?.payrollEnabled);
            await loadAll();
        } catch (e) {
            alert(e.response?.data?.message || t('payroll:settingsErrSave'));
        }
        setSavingSettings(false);
    }

    function openEarn(staff) { setModal({ type: 'earn', staff }); setAmount(''); setNote(''); }
    function openSettle(staff) { setModal({ type: 'settle', staff }); setAmount(''); setNote(''); }

    async function openRate(staff) {
        setModal({ type: 'rate', staff });
        setRateType(staff.rate?.rateType || 'per_lesson');
        setRateAmount(staff.rate?.amount != null ? String(staff.rate.amount) : '');
        setRateFrom(todayStr());
        try {
            const res = await api.get('/staff-payroll/rate/history', { params: { staffMemberId: staff.staffMemberId } });
            setRateHistory(res.data || []);
        } catch (e) { setRateHistory([]); }
    }

    async function openHistory(staff) {
        setModal({ type: 'history', staff });
        try {
            const res = await api.get('/staff-payroll/balance', { params: { staffMemberId: staff.staffMemberId } });
            setBalanceHistory(res.data?.history || []);
        } catch (e) { setBalanceHistory([]); }
    }

    function closeModal() { setModal(null); }

    async function submitEarn() {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { alert(t('payroll:errAmountRequired')); return; }
        setBusy(true);
        try {
            await api.post('/staff-payroll/earn', { staffMemberId: modal.staff.staffMemberId, amount: amt, note: note || null });
            closeModal();
            await loadAll();
        } catch (e) {
            alert(e.response?.data?.message || t('payroll:errAction'));
        }
        setBusy(false);
    }

    async function submitSettle() {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { alert(t('payroll:errAmountRequired')); return; }
        setBusy(true);
        try {
            await api.post('/staff-payroll/settle', { staffMemberId: modal.staff.staffMemberId, amount: amt, note: note || null });
            closeModal();
            await loadAll();
        } catch (e) {
            alert(e.response?.data?.message || t('payroll:errAction'));
        }
        setBusy(false);
    }

    async function submitRate() {
        const amt = parseFloat(rateAmount);
        if (!amt || amt <= 0) { alert(t('payroll:errAmountRequired')); return; }
        setBusy(true);
        try {
            await api.post('/staff-payroll/rate', {
                staffMemberId: modal.staff.staffMemberId,
                rateType,
                amount: amt,
                effectiveFrom: rateFrom
            });
            closeModal();
            await loadAll();
        } catch (e) {
            alert(e.response?.data?.message || t('payroll:errRateSave'));
        }
        setBusy(false);
    }

    const card = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '14px' };
    const input = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', boxSizing: 'border-box' };
    const primaryBtn = { border: 'none', borderRadius: '8px', padding: '8px 12px', background: 'var(--sp-primary)', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 700 };
    const ghostBtn = { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '13px' };
    const successBtn = { border: 'none', borderRadius: '8px', padding: '7px 10px', background: '#059669', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 700 };
    const settleBtn = { border: 'none', borderRadius: '8px', padding: '7px 10px', background: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 700 };

    const tabBtn = (id, label) => (
        <button key={id} type="button" onClick={() => setActiveTab(id)} style={{
            padding: '9px 15px', borderRadius: 10,
            border: activeTab === id ? '2px solid var(--sp-primary)' : '1px solid #cbd5e1',
            background: activeTab === id ? '#fff5f5' : '#fff',
            fontWeight: activeTab === id ? 800 : 600, cursor: 'pointer',
            color: activeTab === id ? 'var(--sp-primary)' : '#334155'
        }}>{label}</button>
    );

    if (loading) {
        return (
            <OwnerPageLayout title={t('payroll:titleLoading')} maxWidth={1200} beforeMain={<DemoViewBanner />}>
                <div className="sp-loading-page">{t('payroll:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('payroll:title')}
            subtitle={t('payroll:subtitle', { name: school?.name || '' })}
            maxWidth={1200}
            beforeMain={<DemoViewBanner />}
        >
            {err && <div style={{ padding: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b', marginBottom: 14 }}>{err}</div>}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {tabBtn('balances', t('payroll:tabBalances'))}
                {tabBtn('reports', t('payroll:tabReports'))}
                {tabBtn('settings', t('payroll:tabSettings'))}
            </div>

            {!payrollEnabled && activeTab !== 'settings' && (
                <div style={{ padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', color: '#92400e', marginBottom: 14, fontSize: 13 }}>
                    {t('payroll:settingsEnableHint')}
                </div>
            )}

            {activeTab === 'settings' && (
                <div className="sp-panel" style={card}>
                    <h3 style={{ marginTop: 0 }}>{t('payroll:settingsEnableLabel')}</h3>
                    <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>{t('payroll:settingsEnableHint')}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontWeight: 700, color: payrollEnabled ? '#059669' : '#64748b' }}>
                            {payrollEnabled ? t('payroll:settingsEnabledOn') : t('payroll:settingsEnabledOff')}
                        </span>
                        <button onClick={toggleEnabled} disabled={savingSettings} style={payrollEnabled ? ghostBtn : primaryBtn}>
                            {payrollEnabled ? t('payroll:settingsToggleOff') : t('payroll:settingsToggleOn')}
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'balances' && (
                <div className="sp-panel" style={card}>
                    {balances.length === 0 && <div style={{ color: '#64748b', padding: '10px 0' }}>{t('payroll:balancesEmpty')}</div>}
                    {balances.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                        <th style={{ padding: '8px 6px' }}>{t('payroll:colStaff')}</th>
                                        <th style={{ padding: '8px 6px' }}>{t('payroll:colRole')}</th>
                                        <th style={{ padding: '8px 6px' }}>{t('payroll:colRate')}</th>
                                        <th style={{ padding: '8px 6px' }}>{t('payroll:colBalance')}</th>
                                        <th style={{ padding: '8px 6px' }}>{t('payroll:colActions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {balances.map(s => (
                                        <tr key={s.staffMemberId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px 6px', fontWeight: 700 }}>{s.fullName}</td>
                                            <td style={{ padding: '8px 6px', color: '#64748b' }}>{roleLabel(s.role)}</td>
                                            <td style={{ padding: '8px 6px' }}>
                                                {s.rate ? (
                                                    <span>{t(`payroll:${RATE_LABEL_KEY[s.rate.rateType]}`)}: {money(s.rate.amount)} {t(`payroll:${RATE_UNIT_KEY[s.rate.rateType]}`)}</span>
                                                ) : <span style={{ color: '#94a3b8' }}>{t('payroll:rateNone')}</span>}
                                                {' '}
                                                <button onClick={() => openRate(s)} style={{ ...ghostBtn, padding: '3px 8px', fontSize: 11 }}>{t('payroll:btnEditRate')}</button>
                                            </td>
                                            <td style={{ padding: '8px 6px', fontWeight: 800, color: s.balance >= 0 ? '#0f172a' : '#b91c1c' }}>{money(s.balance)} ₽</td>
                                            <td style={{ padding: '8px 6px' }}>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    <button onClick={() => openEarn(s)} style={successBtn}>{t('payroll:btnEarn')}</button>
                                                    <button onClick={() => openSettle(s)} style={settleBtn}>{t('payroll:btnSettle')}</button>
                                                    <button onClick={() => openHistory(s)} style={{ ...ghostBtn, padding: '7px 10px', fontSize: 12 }}>{t('payroll:btnHistory')}</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'reports' && (
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        <button type="button" onClick={() => setReportTab('lessons')} style={reportTab === 'lessons' ? primaryBtn : ghostBtn}>{t('payroll:reportLessonsTitle')}</button>
                        <button type="button" onClick={() => setReportTab('earnings')} style={reportTab === 'earnings' ? primaryBtn : ghostBtn}>{t('payroll:reportEarningsTitle')}</button>
                        <button type="button" onClick={() => setReportTab('payments')} style={reportTab === 'payments' ? primaryBtn : ghostBtn}>{t('payroll:reportPaymentsTitle')}</button>
                    </div>
                    <div className="sp-panel sp-toolbar" style={{ marginBottom: 16, padding: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
                            {t('payroll:dateFrom')}
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
                            {t('payroll:dateTo')}
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                        </label>
                        <button type="button" className="sp-btn sp-btn--primary" onClick={() => loadReport(reportTab)}>{t('payroll:refresh')}</button>
                    </div>

                    {reportLoading && <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>{t('common:loading')}</div>}

                    {!reportLoading && reportTab === 'lessons' && (
                        <div className="sp-panel" style={card}>
                            <h3 style={{ marginTop: 0 }}>{t('payroll:reportLessonsTitle')}</h3>
                            <p style={{ color: '#64748b', fontSize: 13 }}>{t('payroll:reportLessonsDesc')}</p>
                            {reportData && (
                                <>
                                    <div style={{ fontSize: 13, color: '#334155', marginBottom: 10 }}>
                                        {t('payroll:lessonsCount', { count: reportData.lessonsCount })} · {t('payroll:totalEarnedLabel', { amount: money(reportData.totalEarned) })}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colDate')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colTime')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colGroup')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colStaff')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colEarnedByAll')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(reportData.lessons || []).map(l => (
                                                    <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '8px 6px' }}>{l.date}</td>
                                                        <td style={{ padding: '8px 6px' }}>{l.startTime}–{l.endTime}</td>
                                                        <td style={{ padding: '8px 6px' }}>{l.groupName}</td>
                                                        <td style={{ padding: '8px 6px' }}>{(l.staff || []).map(s => s.fullName).join(', ')}</td>
                                                        <td style={{ padding: '8px 6px', fontWeight: 700 }}>{money((l.staff || []).reduce((sum, s) => sum + (s.earned || 0), 0))} ₽</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(reportData.lessons || []).length === 0 && <div style={{ color: '#64748b', padding: '12px 0' }}>{t('payroll:noDataPeriod')}</div>}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {!reportLoading && reportTab === 'earnings' && (
                        <div className="sp-panel" style={card}>
                            <h3 style={{ marginTop: 0 }}>{t('payroll:reportEarningsTitle')}</h3>
                            <p style={{ color: '#64748b', fontSize: 13 }}>{t('payroll:reportEarningsDesc')}</p>
                            {reportData && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                                <th style={{ padding: '8px 6px' }}>{t('payroll:colStaff')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('payroll:colLessonEarned')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('payroll:colMonthlyEarned')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('payroll:colManualEarned')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('payroll:colTotal')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(reportData.byStaff || []).map(s => (
                                                <tr key={s.staffMemberId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 6px', fontWeight: 700 }}>{s.fullName}</td>
                                                    <td style={{ padding: '8px 6px' }}>{money(s.lessonEarned)} ₽</td>
                                                    <td style={{ padding: '8px 6px' }}>{money(s.monthlyEarned)} ₽</td>
                                                    <td style={{ padding: '8px 6px' }}>{money(s.manualEarned)} ₽</td>
                                                    <td style={{ padding: '8px 6px', fontWeight: 800 }}>{money(s.total)} ₽</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(reportData.byStaff || []).length === 0 && <div style={{ color: '#64748b', padding: '12px 0' }}>{t('payroll:noDataPeriod')}</div>}
                                </div>
                            )}
                        </div>
                    )}

                    {!reportLoading && reportTab === 'payments' && (
                        <div className="sp-panel" style={card}>
                            <h3 style={{ marginTop: 0 }}>{t('payroll:reportPaymentsTitle')}</h3>
                            <p style={{ color: '#64748b', fontSize: 13 }}>{t('payroll:reportPaymentsDesc')}</p>
                            {reportData && (
                                <>
                                    <div style={{ fontSize: 13, color: '#334155', marginBottom: 10 }}>
                                        {t('payroll:totalPaidLabel', { amount: money(reportData.totalPaid) })}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colDate')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colPaidTo')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colPaidAmount')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colNote')}</th>
                                                    <th style={{ padding: '8px 6px' }}>{t('payroll:colBy')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(reportData.payments || []).map(p => (
                                                    <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '8px 6px' }}>{new Date(p.createdAt).toLocaleString('ru-RU')}</td>
                                                        <td style={{ padding: '8px 6px', fontWeight: 700 }}>{p.fullName}</td>
                                                        <td style={{ padding: '8px 6px' }}>{money(p.amount)} ₽</td>
                                                        <td style={{ padding: '8px 6px', color: '#64748b' }}>{p.note || '—'}</td>
                                                        <td style={{ padding: '8px 6px', color: '#64748b' }}>{p.createdByName || t('payroll:byAuto')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(reportData.payments || []).length === 0 && <div style={{ color: '#64748b', padding: '12px 0' }}>{t('payroll:noDataPeriod')}</div>}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            <Modal
                open={modal?.type === 'earn'}
                onClose={closeModal}
                title={t('payroll:modalEarnTitle', { name: modal?.staff?.fullName })}
                footer={<>
                    <button onClick={closeModal} style={ghostBtn}>{t('payroll:modalCancel')}</button>
                    <button onClick={submitEarn} disabled={busy} style={successBtn}>{t('payroll:modalConfirm')}</button>
                </>}
            >
                <p style={{ color: '#64748b', fontSize: 13 }}>{t('payroll:modalEarnHint')}</p>
                <div style={{ display: 'grid', gap: 8 }}>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:modalAmountLabel')}
                        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={input} autoFocus />
                    </label>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:modalNoteLabel')}
                        <input value={note} onChange={e => setNote(e.target.value)} style={input} />
                    </label>
                </div>
            </Modal>

            <Modal
                open={modal?.type === 'settle'}
                onClose={closeModal}
                title={t('payroll:modalSettleTitle', { name: modal?.staff?.fullName })}
                footer={<>
                    <button onClick={closeModal} style={ghostBtn}>{t('payroll:modalCancel')}</button>
                    <button onClick={submitSettle} disabled={busy} style={settleBtn}>{t('payroll:modalConfirm')}</button>
                </>}
            >
                <p style={{ color: '#64748b', fontSize: 13 }}>{t('payroll:modalSettleHint')}</p>
                <div style={{ display: 'grid', gap: 8 }}>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:modalAmountLabel')}
                        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={input} autoFocus />
                    </label>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:modalNoteLabel')}
                        <input value={note} onChange={e => setNote(e.target.value)} style={input} />
                    </label>
                </div>
            </Modal>

            <Modal
                open={modal?.type === 'rate'}
                onClose={closeModal}
                title={t('payroll:rateModalTitle', { name: modal?.staff?.fullName })}
                footer={<>
                    <button onClick={closeModal} style={ghostBtn}>{t('payroll:modalCancel')}</button>
                    <button onClick={submitRate} disabled={busy} style={primaryBtn}>{t('payroll:rateSave')}</button>
                </>}
            >
                <div style={{ display: 'grid', gap: 8 }}>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:rateTypeLabel')}
                        <select value={rateType} onChange={e => setRateType(e.target.value)} style={input}>
                            {RATE_TYPES.map(rt => <option key={rt} value={rt}>{t(`payroll:${RATE_LABEL_KEY[rt]}`)}</option>)}
                        </select>
                    </label>
                    <label style={{ fontSize: 12, color: '#475569' }}>
                        {t('payroll:rateAmountLabel')} ({t(`payroll:${RATE_UNIT_KEY[rateType]}`)})
                        <input type="number" min="0" step="0.01" value={rateAmount} onChange={e => setRateAmount(e.target.value)} style={input} />
                    </label>
                    <label style={{ fontSize: 12, color: '#475569' }}>{t('payroll:rateEffectiveFromLabel')}
                        <input type="date" value={rateFrom} onChange={e => setRateFrom(e.target.value)} style={input} />
                    </label>
                </div>
                {rateHistory.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t('payroll:rateHistoryTitle')}</div>
                        <div style={{ display: 'grid', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                            {rateHistory.map(r => (
                                <div key={r.id} style={{ fontSize: 12, color: '#64748b', display: 'flex', justifyContent: 'space-between', border: '1px solid #f1f5f9', borderRadius: 6, padding: '4px 8px' }}>
                                    <span>{t(`payroll:${RATE_LABEL_KEY[r.rateType]}`)}: {money(r.amount)} {t(`payroll:${RATE_UNIT_KEY[r.rateType]}`)}</span>
                                    <span>{r.effectiveFrom}{r.effectiveTo ? ` — ${r.effectiveTo}` : ` (${t('payroll:rateCurrentBadge')})`}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={modal?.type === 'history'}
                onClose={closeModal}
                title={t('payroll:historyTitle', { name: modal?.staff?.fullName })}
                footer={<button onClick={closeModal} style={ghostBtn}>{t('common:close', { defaultValue: 'Закрыть' })}</button>}
            >
                {balanceHistory.length === 0 && <div style={{ color: '#64748b' }}>{t('payroll:historyEmpty')}</div>}
                {balanceHistory.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                    <th style={{ padding: '6px' }}>{t('payroll:colDate')}</th>
                                    <th style={{ padding: '6px' }}>{t('payroll:colType')}</th>
                                    <th style={{ padding: '6px' }}>{t('payroll:colAmount')}</th>
                                    <th style={{ padding: '6px' }}>{t('payroll:colNote')}</th>
                                    <th style={{ padding: '6px' }}>{t('payroll:colBy')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {balanceHistory.map(h => (
                                    <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '6px' }}>{new Date(h.createdAt).toLocaleString('ru-RU')}</td>
                                        <td style={{ padding: '6px' }}>{t(`payroll:${TX_TYPE_KEY[h.type] || 'typeManualEarned'}`)}</td>
                                        <td style={{ padding: '6px', fontWeight: 700, color: h.amount >= 0 ? '#059669' : '#b91c1c' }}>{h.amount >= 0 ? '+' : ''}{money(h.amount)} ₽</td>
                                        <td style={{ padding: '6px', color: '#64748b' }}>{h.note || '—'}</td>
                                        <td style={{ padding: '6px', color: '#64748b' }}>{h.createdByName || t('payroll:byAuto')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </OwnerPageLayout>
    );
}
